import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { InvitationsService } from 'core/services/invitations.service';
import { CustomMessageService } from 'core/services/custom-message.service';
import { AuthService } from 'core/services/auth.service';
import { KorviuButtonComponent } from '../../../korviu-button/korviu-button.component';
import { KorviuLoaderComponent } from '../../../korviu-loader/korviu-loader.component';

interface IBulkRemoveReferralsModalData {
  referralIds: number[];
}

export interface IBulkRemoveReferralsModalResult {
  removedCount: number;
}

@Component({
  selector: 'app-bulk-remove-referrals-modal',
  standalone: true,
  imports: [NzModalModule, TranslatePipe, KorviuButtonComponent, KorviuLoaderComponent],
  templateUrl: './bulk-remove-referrals-modal.component.html',
  styleUrl: './bulk-remove-referrals-modal.component.scss'
})
export class BulkRemoveReferralsModalComponent {
  private readonly _modalData = inject<IBulkRemoveReferralsModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject<NzModalRef<BulkRemoveReferralsModalComponent>>(NzModalRef);
  private readonly _invitationService = inject(InvitationsService);
  private readonly _authService = inject(AuthService);
  private readonly _messageService = inject(CustomMessageService);
  private readonly _translateService = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly isLoading = signal(false);
  public readonly selectedCount = this._modalData.referralIds.length;

  public get confirmationKey(): string {
    return this.selectedCount === 1
      ? 'buildYourNetwork.removeReferralConfirmation'
      : 'buildYourNetwork.removeReferralsConfirmation';
  }

  public onCancelClick(): void {
    this._modalRef.destroy();
  }

  public onRemoveClick(): void {
    if (this.isLoading() || !this.selectedCount) {
      return;
    }

    this.isLoading.set(true);
    const requests = this._modalData.referralIds.map(referralId => this._removeReferral(referralId));

    forkJoin(requests)
      .pipe(
        map(results => results.filter((referralId): referralId is number => referralId !== null).length),
        switchMap(removedCount =>
          removedCount
            ? this._authService.getMe(true).pipe(
                catchError(() => of(null)),
                map(() => removedCount)
              )
            : of(removedCount)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(removedCount => {
        this.isLoading.set(false);
        if (removedCount) {
          this._messageService.success(
            this._translateService.instant(
              removedCount === 1 ? 'buildYourNetwork.removeReferralSuccess' : 'buildYourNetwork.removeReferralsSuccess',
              { count: removedCount }
            )
          );
        }

        if (removedCount < this.selectedCount) {
          this._messageService.error(this._translateService.instant('buildYourNetwork.removeReferralsFailure'));
        }

        if (removedCount) {
          this._modalRef.close({ removedCount });
        }
      });
  }

  private _removeReferral(referralId: number): Observable<number | null> {
    return this._invitationService.deleteInvitation(referralId).pipe(
      map(() => referralId),
      catchError(() => of(null))
    );
  }
}
